export type EventStatus =
  | "published"
  | "live"
  | "finished"
  | "cancelled"
  | "interrupted"
  | "participants_synced";

export interface EventMeta {
  title: string;
  scheduledAt: string;
  status: EventStatus;
}

export type AttendanceKind = "confirmed" | "walked_in";

export interface JoinSuccess {
  accessLink: string;
  attendanceKind: AttendanceKind;
  event?: EventMeta;
}

export interface EventHost {
  hostId: string;
  name: string;
  title: string;
  imageUrl: string | null;
  nationality: string;
}

export interface EventLevel {
  code: string;
  label: string;
}

export interface EventAccent {
  code: string;
  label: string;
  flag: string;
}

export interface EventDetail {
  eventId: string;
  title: string;
  description: string;
  coverImageUrl: string | null;
  scheduledAt: string;
  estimatedDuration: number | null;
  status: EventStatus;
  isFull: boolean;
  requirements: string[];
  hosts: EventHost[];
  levels: EventLevel[];
  accent: EventAccent | null;
  /**
   * Backend-reported attendance state. `"confirmed"` means the user has a
   * reserved spot and should ignore `isFull`.
   */
  userAttendanceStatus: string | null;
}

export type JoinErrorCode =
  | "not_confirmed"
  | "event_full"
  | "not_live_yet"
  | "event_ended"
  | "event_cancelled";

export type GateErrorCode =
  | JoinErrorCode
  | "otp_invalid"
  | "otp_expired"
  | "otp_locked"
  | "rate_limited"
  | "network"
  | "event_not_found"
  | "capacity_race"
  | "account_not_found"
  | "subscription_required";

export interface GateError {
  code: GateErrorCode;
  retryAfterSeconds?: number;
  event?: EventMeta;
}

export type JoinInlineError = "network" | "unknown";

export type GateState =
  | { kind: "awaiting_whatsapp" }
  | { kind: "sending_otp"; whatsapp: string }
  | { kind: "awaiting_code"; whatsapp: string; attemptsRemaining: number }
  | { kind: "validating_otp"; whatsapp: string; attemptsRemaining: number }
  | { kind: "fetching_event"; eventId: string }
  | {
      kind: "awaiting_join";
      eventId: string;
      event: EventDetail;
      inlineError: JoinInlineError | null;
      submitting: boolean;
    }
  | { kind: "paywall"; eventId: string; event: EventDetail }
  | { kind: "error"; error: GateError };

export const MAX_OTP_ATTEMPTS = 3;
export const RESEND_COOLDOWN_SECONDS = 60;
